'use client'

import { CheckCircle2, XCircle } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'

interface PasswordChecklistProps {
  password: string
}

export function PasswordChecklist({ password }: PasswordChecklistProps) {
  const t = useTranslations(
    'page.register.registerForm.inputs.password.passwordValidations',
  )
  const [visibleValidations, setVisibleValidations] = useState<
    Record<string, boolean>
  >({})

  const validations = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /\d/.test(password),
    specialChar: /[$&+,:;=?@#|'<>.^*()%!-\s[\]{}_]/.test(password),
  }

  useEffect(() => {
    const newVisibility = Object.fromEntries(
      Object.entries(validations).map(([key, isValid]) => [
        key,
        !isValid, // Mostrar solo las que no cumplen
      ]),
    )
    setVisibleValidations(newVisibility)
  }, [password])

  const allValid = Object.values(validations).every(Boolean)

  return (
    <div className="mt-2 space-y-1 text-sm">
      {!allValid && (
        <p className="text-muted-foreground transition-opacity duration-300">
          {t('requirements')}
        </p>
      )}
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {Object.entries(validations).map(([key, isValid]) => (
          <div
            key={key}
            className={`
              w-[calc(50%-8px)]
              transition-all duration-300 ease-in-out 
              ${visibleValidations[key] ? 'max-h-20 opacity-100 pb-1' : 'max-h-0 opacity-0 pb-0'}
              overflow-hidden
            `}
          >
            <div className="flex items-center gap-2">
              {isValid ? (
                <CheckCircle2 className="size-4 text-green-500 transition-colors" />
              ) : (
                <XCircle className="size-4 text-red-500 transition-colors" />
              )}
              <span className="text-sm">
                {t(key as keyof typeof validations)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
